import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Clock, X, ArrowRight, Smartphone } from "lucide-react";
import QRCode from "react-qr-code";
import { api, formatErr } from "@/lib/api";
import { getGuest, isIpadMode } from "@/lib/guest";
import { useGuestSessionWatch } from "@/lib/useGuestSessionWatch";
import { useIpadLive } from "@/lib/useIpadLive";
import { useTrackView } from "@/lib/analyticsTracker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import GuestHeader from "@/components/GuestHeader";
import BookingAnswers from "@/components/BookingAnswers";
import PendingInviteChip from "@/components/PendingInviteChip";
import GuestNotificationChip from "@/components/GuestNotificationChip";

export default function GuestReservations() {
  useGuestSessionWatch();
  useTrackView("reservations");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  // Personal QR dialog state. `qrOpen` toggles the modal; `qrToken` holds the
  // just-minted short-lived JWT (10 min TTL). Only rendered while the guest is
  // on the iPad — a personal phone would have nothing to hand off to itself.
  const [qrOpen, setQrOpen] = useState(false);
  const [qrToken, setQrToken] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrErr, setQrErr] = useState("");
  const [mobileUrl, setMobileUrl] = useState("");
  const navigate = useNavigate();
  const { name: guestName, id: guestId, cabin_id: cabinId, ipad_token: ipadToken, roster } = getGuest();
  const onIpad = isIpadMode();
  // Locate this guest's PIN out of the cabin roster — iPad session already
  // ships the roster with `pin` fields, so no extra network hop needed.
  const myPin = (roster || []).find((r) => r.guest_id === guestId)?.pin || "";

  const load = async (silent = false) => {
    if (!guestId) { navigate("/"); return; }
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/public/bookings/mine", { params: { guest_id: guestId } });
      setBookings(data || []);
    } catch (e) {
      // Silent background polls (60 s cadence + visibilitychange) should never
      // spam the guest with a toast — one transient blip used to fire this once
      // a minute for the whole charter. Only surface on the initial user-driven
      // load, mirroring the CabinView polling fix.
      if (!silent) {
        toast.error(formatErr(e.response?.data?.detail) || "Could not load reservations");
      } else {
        // eslint-disable-next-line no-console
        console.debug("[GuestReservations] silent poll failed:", e?.message || e);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Push-based live sync on the shared iPad — when the crew confirms,
  // reschedules or cancels a booking from the admin dashboard, the
  // backend fans a `booking.*` frame down this cabin's iPad socket.
  // We silent-refetch so the guest never has to pull-to-refresh. Also
  // catches `charter.updated` (their embark/disembark window changed)
  // and `guest.onboard_changed` (they were flipped to Ashore).
  useIpadLive((ev) => {
    if (!ev?.type) return;
    if (ev.type.startsWith("booking.") || ev.type === "charter.updated" || ev.type === "guest.onboard_changed") {
      load(true);
    }
  });

  // 60 s safety poll — belt-and-braces catch for the (rare) case where
  // the iPad WebSocket dropped silently and a push never arrived. Only
  // runs on the shared kiosk; personal guest phones already have
  // `useGuestSessionWatch` for session validity and a manual refresh.
  useEffect(() => {
    if (!isIpadMode()) return;
    const id = setInterval(() => load(true), 60_000);
    const onVis = () => { if (document.visibilityState === "visible") load(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull the deployed guest URL from public settings — used as the base for
  // the personal QR (falls back to window.location.origin so the QR still
  // works on preview even before Settings is configured).
  useEffect(() => {
    api.get("/public/settings")
      .then(({ data }) => setMobileUrl((data.guest_mobile_url || "").trim() || window.location.origin))
      .catch(() => setMobileUrl(window.location.origin));
  }, []);

  const openQr = async () => {
    if (!onIpad || !cabinId || !ipadToken || !guestId) {
      toast.error("Personal QR is only available on the cabin iPad.");
      return;
    }
    setQrOpen(true);
    setQrLoading(true);
    setQrErr("");
    setQrToken("");
    try {
      const { data } = await api.post("/public/ipad/mobile-resume-token", {
        cabin_id: cabinId,
        session_token: ipadToken,
        guest_id: guestId,
      });
      setQrToken(data.resume_token || "");
    } catch (e) {
      setQrErr(formatErr(e.response?.data?.detail) || "Could not mint the QR right now.");
    } finally {
      setQrLoading(false);
    }
  };

  const qrValue = qrToken && mobileUrl
    ? `${mobileUrl}${mobileUrl.includes("?") ? "&" : "?"}resume=${qrToken}`
    : "";

  const cancel = async (b) => {
    if (!window.confirm(`Cancel "${b.treatment_name}" on ${new Date(b.start_at).toLocaleString()}?`)) return;
    try {
      await api.post(`/public/bookings/${b.id}/cancel`, null, { params: { guest_id: guestId } });
      toast.success("Reservation cancelled");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not cancel");
    }
  };

  const clearCancelled = async () => {
    if (!window.confirm(`Clear ${cancelled.length} cancelled reservation${cancelled.length === 1 ? "" : "s"} from your view? The crew still keeps the audit trail.`)) return;
    try {
      const { data } = await api.post("/public/bookings/clear-cancelled", { guest_id: guestId });
      toast.success(`Cleared ${data.cleared || 0} from your history`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not clear");
    }
  };

  const now = new Date();
  const upcoming = bookings.filter((b) => b.status !== "cancelled" && new Date(b.start_at) >= now);
  const past = bookings.filter((b) => b.status !== "cancelled" && new Date(b.start_at) < now);
  const cancelled = bookings.filter((b) => b.status === "cancelled");

  const Section = ({ title, items, allowCancel, hideHeader }) => (
    <section className="mb-14" data-testid={`section-${title.toLowerCase()}`}>
      {!hideHeader && <div className="overline text-primary/50 mb-4">{title} · {items.length}</div>}
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border p-6">Nothing here.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((b) => (
            <li key={b.id} className="bg-card border border-border p-5 flex flex-col gap-3" data-testid={`reservation-${b.id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl truncate">{b.treatment_name}</div>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-2">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{new Date(b.start_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(b.start_at).toTimeString().slice(0,5)} · {b.duration_min} min</span>
                    <span className="text-accent font-medium">€{Number(b.price || 0).toFixed(0)}</span>
                  </div>
                </div>
                {allowCancel && (
                  <Button size="sm" variant="outline" onClick={() => cancel(b)} className="text-destructive border-destructive/40 rounded-full" data-testid={`cancel-${b.id}`}>
                    <X className="h-3 w-3 mr-1" /> Cancel
                  </Button>
                )}
              </div>
              <BookingAnswers answers={b.answers} compact />
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <div className="min-h-screen bg-background">
      <GuestHeader guestName={guestName} />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="mb-6 space-y-2">
          <GuestNotificationChip guestId={guestId} />
          <PendingInviteChip guestId={guestId} />
        </div>
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="overline text-accent mb-3">Your reservations</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight">Welcome back, {guestName || "guest"}.</h1>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {onIpad && (
              <Button
                onClick={openQr}
                variant="outline"
                className="rounded-full border-accent/40 text-accent hover:bg-accent/10"
                data-testid="show-my-qr-btn"
                title="Connect this reservation from your phone"
              >
                <Smartphone className="h-4 w-4 mr-2" /> My phone QR
              </Button>
            )}
            <Button onClick={() => navigate("/experience")} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-booking-btn">
              New booking <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : (
          <>
            <Section title="Upcoming" items={upcoming} allowCancel />
            {past.length > 0 && <Section title="Past" items={past} />}
            {cancelled.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="overline text-primary/50">Cancelled · {cancelled.length}</div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearCancelled}
                    className="text-xs text-muted-foreground hover:text-destructive rounded-full h-8"
                    data-testid="clear-cancelled-btn"
                  >
                    <X className="h-3 w-3 mr-1" /> Clear from my view
                  </Button>
                </div>
                <Section title="Cancelled" items={cancelled} hideHeader />
              </>
            )}
            {upcoming.length === 0 && past.length === 0 && cancelled.length === 0 && (
              <div className="text-center py-16" data-testid="no-reservations">
                <p className="text-muted-foreground mb-6">You have no reservations yet.</p>
                <Button onClick={() => navigate("/experience")} className="rounded-full bg-primary hover:bg-primary/90">
                  Browse the menu <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Personal QR + PIN dialog — iPad only. Handing the phone the freshly
          minted personal token means the mobile is auto-signed in as this
          guest without a roster pick or PIN entry on the phone. */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-sm" data-testid="my-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Sign in on your phone</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Scan this code with your phone's camera — you'll land straight on the menu as <span className="text-foreground font-medium">{guestName}</span>, no PIN required.
              The QR is valid for 10 minutes; reopen this dialog to get a fresh one.
            </p>
            <div className="flex justify-center">
              {qrLoading ? (
                <div className="w-[220px] h-[220px] flex items-center justify-center text-muted-foreground text-sm border border-dashed border-border" data-testid="my-qr-loading">
                  Preparing your QR…
                </div>
              ) : qrValue ? (
                <div className="flex flex-col items-center gap-3" data-testid="my-qr-image">
                  <div className="bg-white p-3 border border-border">
                    <QRCode value={qrValue} size={220} level="M" style={{ height: 220, width: 220 }} />
                  </div>
                  {mobileUrl && (
                    <div className="text-center max-w-[260px]" data-testid="my-qr-url">
                      <div className="overline text-primary/60 text-[10px] mb-1">Or type in your browser</div>
                      <a
                        href={mobileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-accent underline decoration-dotted underline-offset-2 break-all font-mono"
                      >
                        {mobileUrl}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-[220px] h-[220px] flex items-center justify-center text-destructive text-sm border border-destructive/40 p-4 text-center" data-testid="my-qr-error">
                  {qrErr || "Could not mint the QR."}
                </div>
              )}
            </div>
            {myPin && (
              <div className="border border-border bg-muted/40 p-4">
                <div className="overline text-primary/60 text-[10px] mb-2">Or type your PIN</div>
                <div className="flex items-baseline justify-between gap-4">
                  <div className="text-xs text-muted-foreground">
                    Cabin sign-in for<br />
                    <span className="text-foreground font-medium">{guestName}</span>
                  </div>
                  <div className="font-mono text-3xl tracking-[0.35em] text-accent select-all" data-testid="my-qr-pin">
                    {myPin}
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3 italic">
                  This PIN is personal — please keep it to yourself.
                </p>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setQrOpen(false)} className="rounded-full" data-testid="my-qr-close">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
